export const PAGE_ENTRANCE_TIMING = Object.freeze({
  duration: 680,
  stagger: 55,
  maxDelay: 330,
  easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
});

const EXCLUDED_GROUP_SELECTOR = [
  '[hidden]',
  '[aria-hidden="true"]',
  '.motion-logistics-background',
  'script',
  'style',
].join(',');

const HEADER_GROUP_SELECTOR = [
  'h1',
  '.page-header',
  '.workflow-action-header',
  '.customer-dashboard-header',
].join(',');

const INSPECTOR_GROUP_SELECTOR = [
  '.detail-drawer',
  '.inspector',
  '[data-inspector]',
  '[role="dialog"]',
].join(',');

function isVisibleGroup(element) {
  return element?.nodeType === 1 && !element.matches(EXCLUDED_GROUP_SELECTOR);
}

export function collectPageEntranceGroups(container) {
  const directGroups = Array.from(container?.children ?? []).filter(isVisibleGroup);
  const dashboardLayout = directGroups.find((element) => element.matches?.('.operations-dashboard-layout'));
  if (dashboardLayout?.querySelectorAll) {
    const dashboardGroups = Array.from(dashboardLayout.querySelectorAll([
      '.dashboard-metrics > *',
      '.container-operations-panel',
      '.dashboard-secondary-data',
    ].join(','))).filter(isVisibleGroup);
    if (dashboardGroups.length) {
      return [
        ...dashboardGroups,
        ...directGroups.filter((element) => element !== dashboardLayout),
      ];
    }
  }
  if (directGroups.length !== 1) return directGroups;

  const onlyGroup = directGroups[0];
  if (!onlyGroup.matches('main, section, .workflow-page, .container-task-detail')) {
    return directGroups;
  }

  const nestedGroups = Array.from(onlyGroup.children ?? []).filter(isVisibleGroup);
  return nestedGroups.length > 1 ? nestedGroups : directGroups;
}

export function pageEntranceDirection(element) {
  if (element?.matches?.(HEADER_GROUP_SELECTOR)) return 'top';
  if (element?.matches?.(INSPECTOR_GROUP_SELECTOR)) return 'right';
  return 'bottom';
}

export function pageEntranceKeyframes(direction = 'bottom', {
  preserveTextSharpness = false,
  preserveViewportFixedChildren = false,
} = {}) {
  if (preserveViewportFixedChildren) {
    return [
      { opacity: 0, transform: 'none', filter: 'none' },
      { opacity: 1, transform: 'none', filter: 'none' },
    ];
  }
  const offsets = {
    top: { x: 0, y: -18 },
    bottom: { x: 0, y: 26 },
    left: { x: -24, y: 0 },
    right: { x: 24, y: 0 },
  };
  const offset = offsets[direction] ?? offsets.bottom;
  if (preserveTextSharpness) {
    return [
      {
        opacity: 0,
        transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
        filter: 'none',
      },
      {
        opacity: 1,
        transform: 'translate3d(0, 0, 0)',
        filter: 'none',
      },
    ];
  }
  return [
    {
      opacity: 0,
      transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(0.985)`,
      filter: 'blur(8px)',
    },
    {
      opacity: 1,
      transform: 'translate3d(0, 0, 0) scale(1)',
      filter: 'blur(0)',
    },
  ];
}

/**
 * Motion removed product-wide; loading indicators only. This stub keeps the
 * mount API but performs no animation: it synchronously stamps the page's
 * data-page-entrance-state attribute as 'complete' (same contract as before)
 * and returns a no-op cleanup function. No element.animate, no timers.
 */
export function mountPageEntrance({ container, window: windowObject = globalThis.window } = {}) {
  void windowObject;
  if (!container) return () => {};
  if (container.dataset) container.dataset.pageEntranceState = 'complete';
  else container.setAttribute?.('data-page-entrance-state', 'complete');
  return () => {};
}
